import { Capacitor } from '@capacitor/core'

/**
 * Vrai quand ce paquet est CONSTRUIT pour la coquille native (`npm run build:native`).
 *
 * 🔴 NE PAS CONFONDRE AVEC `isNativeApp()` — la distinction est le cœur du chantier :
 *
 *  - `isNativeApp()` répond « où est-ce que je m'exécute ? » → sert à ADAPTER un
 *    comportement à l'exécution (ex. ne pas proposer une bannière de mise à jour).
 *  - `CIBLE_NATIVE` répond « qu'est-ce qui existe dans ce paquet ? » → sert à FAIRE
 *    DISPARAÎTRE du binaire ce qui ne doit pas s'y trouver.
 *
 * Apple sanctionne (Guideline 5.6) une fonctionnalité **présente mais masquée** : elle
 * « semble cachée intentionnellement ». Une fonctionnalité **absente**, elle, ne peut pas
 * être révélée après la review — et c'est vérifiable dans le paquet livré. C'est pourquoi
 * les surfaces d'ACHAT doivent être gouvernées par cette constante, et pas par un test
 * d'exécution. La valeur étant remplacée littéralement à la compilation, le compilateur
 * élimine le code devenu inatteignable.
 *
 * ⚠️ Les deux gardes se CUMULENT volontairement sur les surfaces marchandes tant que la
 * coquille charge encore le site distant (`server.url`) : dans cette configuration, l'app
 * native exécute le paquet WEB, où `CIBLE_NATIVE` vaut faux. Retirer `isNativeApp()` de
 * ces endroits AVANT d'avoir coupé `server.url` rendrait donc les prix visibles dans
 * l'app — une régression de conformité immédiate. Le second garde ne deviendra redondant
 * qu'une fois le cordon coupé, et il ne coûte rien.
 */
export const CIBLE_NATIVE = import.meta.env.VITE_CIBLE_NATIVE === '1'

/** Marqueur injecté dans le User-Agent par le shell natif (capacitor.config → appendUserAgent). */
const NATIVE_UA_MARKER = /MondalonApp/i

/**
 * true quand l'app tourne dans le shell natif Capacitor (iOS / Android).
 * Modèle « reader-app » : dans l'app native, on masque TOUT élément marchand
 * (prix, abonnement, « S'abonner », checkout, portail facturation). Le parcours
 * d'abonnement et de paiement vit exclusivement sur le web (mondalon.net).
 * En navigateur web classique, retourne false → comportement inchangé.
 *
 * Détection PRIORITAIRE par User-Agent : déterministe dès le 1er render, indépendante
 * du runtime Capacitor. `Capacitor.isNativePlatform()` n'est PAS fiable en mode hosted
 * (server.url distant : le bridge natif peut être absent/injecté tardivement dans la page
 * distante) → cause racine des rejets Apple 3.1.1 répétés. Le test Capacitor reste en
 * secours (ancien comportement) au cas où l'UA serait indisponible. (2026-07-18)
 */
export const isNativeApp = (): boolean => {
  try {
    if (typeof navigator !== 'undefined' && NATIVE_UA_MARKER.test(navigator.userAgent)) return true
  } catch { /* navigator indisponible (SSR / contexte exotique) — on retombe sur Capacitor */ }
  return Capacitor.isNativePlatform()
}

/**
 * true quand l'app tourne en PWA installée (« Ajouter à l'écran d'accueil »), affichée
 * en standalone — PAS le shell natif Capacitor (cf. isNativeApp ci-dessus).
 *
 * Utilité : le magic link ouvre le navigateur par défaut (Safari/Chrome), séparé du
 * contexte d'exécution de la PWA installée — même problème de webview isolé que l'app
 * native, qui a motivé le flux OTP par code (cf. Login.tsx). Sans détection dédiée, une
 * PWA se voyait proposer uniquement le lien magique (branche « web » par défaut), sans
 * jamais afficher le champ code. (2026-07-21, signalé David)
 *
 * `display-mode: standalone` = standard PWA (Android/desktop) ; `navigator.standalone`
 * = spécifique iOS Safari (PWA ajoutée à l'écran d'accueil, avant l'unification de la
 * media query sur iOS).
 */
export const isStandalonePwa = (): boolean => {
  try {
    if (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) return true
    if (typeof navigator !== 'undefined' && (navigator as any).standalone === true) return true
  } catch { /* matchMedia/navigator indisponible (SSR) */ }
  return false
}
